import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { WORKFLOW_STEPS } from '../data/companyData';
import { WorkflowVideoPlayer } from './WorkflowVideoPlayer';
import {
  FileSearch,
  BarChart2,
  Calendar,
  Cpu,
  CheckCircle2,
  MessageSquare,
  Send,
  Sparkles,
  Clock,
  ShieldCheck,
  Zap
} from 'lucide-react';

interface WorkflowSectionProps {
  onOpenContact?: () => void;
}

export const WorkflowSection: React.FC<WorkflowSectionProps> = ({ onOpenContact }) => {
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);

  const handleSelectStep = (idx: number) => {
    setActiveStepIndex(idx);
  };

  const getStepIcon = (name: string) => {
    switch (name) {
      case 'FileSearch': return FileSearch;
      case 'BarChart2': return BarChart2;
      case 'Calendar': return Calendar;
      case 'Cpu': return Cpu;
      case 'CheckCircle2': return CheckCircle2;
      case 'MessageSquare': return MessageSquare;
      default: return Send;
    }
  };

  return (
    <section id="workflow" className="py-16 sm:py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center max-w-3xl mx-auto space-y-4"
        >
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold uppercase tracking-wider">
            <Zap className="w-3.5 h-3.5 text-emerald-600" /> Automated Production Engine
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Our Streamlined 7-Step Workflow
          </h2>
          <p className="text-slate-600 text-base sm:text-lg">
            From manuscript intake to press-ready delivery, our AI-assisted production pipeline ensures total transparency and rapid turnaround.
          </p>
        </motion.div>

        {/* Horizontal Workflow Line & Cards Container */}
        <div className="mt-12 relative">
          
          {/* Connecting Animated Line (Desktop) */}
          <div className="hidden lg:block absolute top-12 left-8 right-8 h-1 bg-slate-200 z-0">
            <div
              className="h-full bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 transition-all duration-500 rounded-full"
              style={{ width: `${((activeStepIndex + 1) / WORKFLOW_STEPS.length) * 100}%` }}
            />
          </div>

          {/* Workflow Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4 relative z-10">
            {WORKFLOW_STEPS.map((step, idx) => {
              const IconComp = getStepIcon(step.iconName);
              const isActive = activeStepIndex === idx;

              return (
                <motion.div
                  key={step.stepNumber}
                  initial={{ opacity: 0, y: 30, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, margin: '-20px' }}
                  transition={{ duration: 0.45, delay: idx * 0.07, ease: 'easeOut' }}
                  onClick={() => handleSelectStep(idx)}
                  className={`p-4 rounded-2xl border transition-all duration-300 cursor-pointer flex flex-col justify-between relative group ${
                    isActive
                      ? 'bg-gradient-to-b from-blue-900 via-indigo-900 to-purple-950 text-white border-blue-500 shadow-xl shadow-blue-900/20 scale-[1.03] ring-2 ring-blue-400/50'
                      : 'bg-slate-50 hover:bg-white text-slate-800 border-slate-200/90 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  {/* Step Badge */}
                  <div className="flex items-center justify-between mb-3">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-extrabold ${
                      isActive ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-700 group-hover:bg-blue-600 group-hover:text-white'
                    }`}>
                      0{step.stepNumber}
                    </span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      isActive ? 'bg-white/20 text-blue-200' : 'bg-slate-200 text-slate-600'
                    }`}>
                      {step.sla}
                    </span>
                  </div>

                  {/* Icon */}
                  <div className={`p-2.5 rounded-xl w-10 h-10 flex items-center justify-center mb-3 ${
                    isActive ? 'bg-white/10 text-blue-300' : 'bg-blue-50 text-blue-600'
                  }`}>
                    <IconComp className="w-5 h-5" />
                  </div>

                  {/* Title */}
                  <div>
                    <h3 className={`text-sm font-bold leading-tight ${isActive ? 'text-white' : 'text-slate-900'}`}>
                      {step.title}
                    </h3>
                    <p className={`text-[11px] mt-1 line-clamp-2 ${isActive ? 'text-slate-300' : 'text-slate-500'}`}>
                      {step.shortDesc}
                    </p>
                  </div>

                  {/* Active Marker Dot */}
                  {isActive && (
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-3 h-3 bg-blue-500 rounded-full ring-4 ring-white shadow-md hidden lg:block" />
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Active Step Detailed Inspector Box */}
          <AnimatePresence mode="wait">
            {WORKFLOW_STEPS[activeStepIndex] && (
              <motion.div
                key={activeStepIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                role="region"
                aria-live="polite"
                aria-atomic="true"
                className="mt-8 bg-slate-900 text-white rounded-2xl p-6 sm:p-8 border border-slate-800 shadow-2xl relative overflow-hidden"
              >
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                  
                  <div className="space-y-3 flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="px-3 py-1 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-xs font-bold uppercase tracking-wider">
                        Step {WORKFLOW_STEPS[activeStepIndex].stepNumber} of 7
                      </span>
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5 text-blue-400" /> Standard SLA: {WORKFLOW_STEPS[activeStepIndex].sla}
                      </span>
                    </div>

                    <h3 className="text-2xl font-bold text-white">
                      {WORKFLOW_STEPS[activeStepIndex].title}
                    </h3>

                    <p className="text-sm text-slate-300 leading-relaxed max-w-3xl">
                      {WORKFLOW_STEPS[activeStepIndex].fullDesc}
                    </p>
                  </div>

                  <div className="w-full md:w-auto flex flex-col sm:flex-row md:flex-col gap-3 shrink-0">
                    <div className="p-3.5 rounded-xl bg-slate-800/80 border border-slate-700/80 space-y-1">
                      <p className="text-[11px] text-purple-300 font-bold uppercase tracking-wide flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> AI Enhancement
                      </p>
                      <p className="text-xs text-slate-200 font-medium">
                        {WORKFLOW_STEPS[activeStepIndex].aiEnhancement}
                      </p>
                    </div>

                    <div className="p-3.5 rounded-xl bg-slate-800/80 border border-slate-700/80 space-y-1">
                      <p className="text-[11px] text-emerald-300 font-bold uppercase tracking-wide flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> Quality Gate
                      </p>
                      <p className="text-xs text-slate-200 font-medium">
                        {WORKFLOW_STEPS[activeStepIndex].qualityGate}
                      </p>
                    </div>
                  </div>

                </div>
              </motion.div>
            )}
          </AnimatePresence>

        </div>

        {/* Video Placeholder & Interactive Promotional Explainer */}
        <WorkflowVideoPlayer onOpenContact={onOpenContact} />

      </div>
    </section>
  );
};

